<template>
  <base-legal-page
    kicker="Dad Jokes Vault"
    title="Dad Jokes Vault Privacy Policy"
    updated="September 2026"
  >
    <h2>1. The short version</h2>
    <p>
      Dad Jokes Vault has no accounts and no sign-in, and it does not collect personal information about you. Four things leave your device: what is needed to process a purchase (handled by Apple and our payments provider), a once-a-day check for a newer version, anonymous usage counts — tied to a random token, not to you — and, only when you tap Read aloud on a joke that has one, a request for that joke's audio clip.
    </p>

    <h2>2. Information we collect</h2>
    <p>
      <strong>No personal information.</strong> Dad Jokes Vault does not ask for your name, your email address, or any other personal detail, and there is nothing to sign in to. We do not build a profile of you and we do not use advertising or tracking software.
    </p>
    <p>
      <strong>Anonymous usage events.</strong> The app sends our own server (api.hackerhang.com) short event records: an event name from a fixed list — the app was opened, a punchline was revealed, a joke was bookmarked, a clip was played — with a few labels such as the joke's id or a count, plus the app version and platform. Each install generates a random token on first launch so we can count how many installs are active; it is not your device identifier, not the advertising identifier, and it links to nothing about you. Events never contain anything you typed, read, or said.
    </p>
    <p>
      <strong>Update check.</strong> Once a day the app asks the same server whether a newer version exists, sending only the app version and platform.
    </p>

    <h2>3. What stays on your device</h2>
    <p>
      The app saves a small amount of information locally so that it works the way you would expect:
    </p>
    <ul>
      <li><strong>Your bookmarks:</strong> the jokes you have saved</li>
      <li><strong>Your reading history:</strong> which jokes you have seen, so the daily three stay fresh and About can show how many jokes you have read</li>
      <li><strong>Your settings:</strong> preferences such as sound effects</li>
    </ul>
    <p>
      All of this is stored on your device only. It is never uploaded, never backed up to us, and never shared. We cannot see it. Deleting the app deletes all of it.
    </p>

    <h2>4. Read aloud</h2>
    <p>
      Some jokes can be read aloud in two voices, Dad and Kid, recorded ahead of time with ElevenLabs — the app never listens to you and never uses your device's built-in voices. When you tap Read aloud, the app downloads that joke's audio clip over a plain HTTPS request to a public file store (Cloudflare R2) and keeps it on your phone so it does not need to be downloaded again. That request carries nothing about you, only the joke's id as part of the file's address. Read aloud only appears on jokes that have a recorded clip; we are adding more over time.
    </p>

    <h2>5. Purchases</h2>
    <p>
      Dad Jokes Vault offers a one-time in-app purchase that unlocks the full library. Purchases are processed by Apple, under Apple's own privacy policy. We never see your payment details.
    </p>
    <p>
      To remember that you have bought the app across reinstalls and devices, we use RevenueCat, a purchase-management service. RevenueCat receives an anonymous identifier generated on your device, together with the purchase receipt Apple issues. We also attach the same random install token described above as a customer attribute, so that restoring your purchase on a new install can be matched to your prior purchase. RevenueCat does not receive your name, your email address, or anything you have read, bookmarked, or done in the app. Their privacy policy is at
      <a
        href="https://www.revenuecat.com/privacy"
        target="_blank"
        rel="noopener"
      >revenuecat.com/privacy</a>.
    </p>
    <p>
      Apart from purchases, the update check, the anonymous events, and clip downloads above, the app makes no network requests.
    </p>

    <h2>6. Permissions</h2>
    <p>
      Dad Jokes Vault does not access your photos, contacts, location, calendar, or health data. It does not request the microphone and never records or listens to anything — Read aloud plays back pre-recorded clips, it does not turn your voice into anything. The app does not use the advertising identifier and does not track you across other apps or websites.
    </p>

    <h2>7. Children's privacy</h2>
    <p>
      Dad Jokes Vault is a joke app intended for a general audience, including children. The app collects no personal information from anyone, including children under 13: nothing you read, bookmark, reveal, or type ever leaves your phone, and the only things that do leave it are the anonymous counts, the update check, and clip downloads described above.
    </p>

    <h2>8. Your rights</h2>
    <p>
      The usage events we hold are anonymous and cannot be matched to you, so there is nothing for us to show you, correct, or delete about you. You can remove everything the app has stored on your phone, including the random install token, by deleting the app. If you have bought the full library and later reinstall, "Restore" on the About screen will recover it through Apple.
    </p>

    <h2>9. Changes to this policy</h2>
    <p>
      If the app ever begins collecting information beyond what is described here, this page will be updated before that change ships, and the "Last updated" date above will change with it.
    </p>

    <h2>10. Contact us</h2>
    <p>If you have any questions about this policy, please get in touch:</p>
    <div class="hh-legal__contact">
      <div><strong>Email:</strong> <a href="mailto:info@hackerhang.com">info@hackerhang.com</a></div>
      <div><strong>Website:</strong> <a href="https://hackerhang.com">hackerhang.com</a></div>
    </div>
  </base-legal-page>
</template>

<script>
  export default {
    name: 'DadjokesPrivacy',

    metaInfo: { title: 'Dad Jokes Vault Privacy Policy' },
  }
</script>
